/* ────────────────────────────────────────────────────────────────
   Shared chart theme + math helpers.

   Every colour resolves from the CSS custom properties declared in
   src/styles/App.css, so charts follow the app theme instead of
   carrying their own hard-coded palette.
   ──────────────────────────────────────────────────────────────── */

/* Read a CSS custom property off :root (falls back during SSR/tests). */
export const cssVar = (name, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
};

/* Dark → light pairs used to build vertical bar gradients. */
export const GRADIENT_TONES = {
  violet: ["--violet-dark", "--violet-light"],
  green: ["--green-dark", "--green-light"],
  blue: ["--blue-dark", "--blue-light"],
  pink: ["--pink-dark", "--pink-light"],
  orange: ["--orange-dark", "--orange-light"],
  lime: ["--lime-dark", "--lime-light"],
  red: ["--red-dark", "--red-light"],
};

/* Order used when a chart cycles tones automatically (one per bar). */
export const TONE_CYCLE = ["violet", "green", "pink", "orange", "lime", "red"];

/* Flat categorical colours for donut slices, segmented bars and legends. */
export const SERIES_VARS = [
  "--chart-series-1",
  "--chart-series-2",
  "--chart-series-3",
  "--chart-series-4",
  "--chart-series-5",
  "--chart-series-6",
];

const wrap = (index, length) => {
  const n = Number.isFinite(index) ? index : 0;
  return ((n % length) + length) % length;
};

/* Nth categorical colour, wrapping around the palette. */
export const seriesColor = (index) =>
  cssVar(SERIES_VARS[wrap(index, SERIES_VARS.length)]);

/**
 * Normalise a tone into `{ dark, light }`.
 * Accepts: a palette name ("green"), a palette index (0, 1, 2…),
 * `{ darkVar, lightVar }` custom-property names, or `{ dark, light }` literals.
 */
export const resolveTone = (tone) => {
  if (tone && typeof tone === "object") {
    if (tone.dark) return { dark: tone.dark, light: tone.light || tone.dark };
    if (tone.darkVar) {
      const dark = cssVar(tone.darkVar);
      return { dark, light: cssVar(tone.lightVar) || dark };
    }
  }
  const key =
    typeof tone === "number"
      ? TONE_CYCLE[wrap(tone, TONE_CYCLE.length)]
      : tone || TONE_CYCLE[0];
  const [darkVar, lightVar] = GRADIENT_TONES[key] || GRADIENT_TONES.violet;
  return { dark: cssVar(darkVar), light: cssVar(lightVar) };
};

/**
 * Top-to-bottom gradient across the plot area.
 * Always derived from the (stable) chartArea rather than per-bar geometry:
 * freshly created bars sit at the baseline while Chart.js resolves options,
 * which would collapse a bar-local gradient to a flat colour.
 */
export const verticalGradient = (chart, tone) => {
  const { ctx, chartArea } = chart || {};
  const { dark, light } = resolveTone(tone);
  // Layout not measured yet — return solid; Chart.js re-resolves after layout.
  if (!ctx || !chartArea) return dark;
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, dark);
  gradient.addColorStop(1, light);
  return gradient;
};

/* Neutral colours for axes, grid lines and tooltips. */
export const chartColors = () => ({
  ink: cssVar("--chart-ink", "#111827"),
  label: cssVar("--chart-label", "#374151"),
  muted: cssVar("--chart-muted", "#6b7280"),
  subtle: cssVar("--chart-subtle", "#9ca3af"),
  grid: cssVar("--chart-grid", "#e5e7eb"),
  tooltipBg: cssVar("--chart-tooltip-bg", "#1f2937"),
  onDark: cssVar("--chart-on-dark", "#ffffff"),
});

/* 4000 → "4K", 125000 → "1.25L", 12000000 → "1.2Cr" (Indian numbering). */
export const formatCompactNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "0";
  if (num >= 1e7) return `${(num / 1e7).toFixed(num % 1e7 === 0 ? 0 : 2)}Cr`;
  if (num >= 1e5) return `${(num / 1e5).toFixed(num % 1e5 === 0 ? 0 : 2)}L`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(num % 1e3 === 0 ? 0 : 1)}K`;
  return num.toString();
};

/* Divisor + suffix so large values can be plotted in K / L / Cr units. */
export const pickUnit = (maxValue) => {
  if (maxValue >= 1e7) return { div: 1e7, suffix: "Cr" };
  if (maxValue >= 1e5) return { div: 1e5, suffix: "L" };
  if (maxValue >= 1e3) return { div: 1e3, suffix: "K" };
  return { div: 1, suffix: "" };
};

/* Round a data maximum up to a readable axis max + step (5 ticks). */
export const niceAxis = (value) => {
  if (value <= 0) return { max: 10, step: 2 };
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const n = value / pow;
  const niceN = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  const max = niceN * pow;
  return { max, step: max / 5 };
};

/* Deep-merge caller overrides over the computed Chart.js options.
   Arrays and functions are replaced wholesale, plain objects merge. */
export const mergeOptions = (base, override) => {
  if (!override) return base;
  const out = { ...base };
  Object.keys(override).forEach((key) => {
    const a = base?.[key];
    const b = override[key];
    const mergeable =
      b && a && typeof b === "object" && typeof a === "object" && !Array.isArray(b);
    out[key] = mergeable ? mergeOptions(a, b) : b;
  });
  return out;
};
